import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTable, usePagination } from "react-table";
import { COLUMNS } from "./columns";
import { noDataPresent } from "../../../../../locales/en";
import TableSkeleton from "../../../../skeletons/TableSkeleton";
import DualPagination from "../../../../common/DualPagination";
import SortableHeaderCell from "../../../../common/SortableHeaderCell";
import {
  PAGE_SIZE_50,
  PAGE_SIZE_OPTIONS,
  ZERO_NUMBER,
  NUMBER_ONE,
  SKELETON_ROWS_10
} from "../../../../../utils/constants/appConstants";
import "./../../reportable/reportable.scss";
import "./accountsReceivableTable.scss";

/**
 * The Accounts Receivable rows. The server holds the whole result set and returns one
 * page at a time, so paging and sorting are both server side.
 */
const AccountsReceivableTable = ({
  data,
  loading,
  totalPages = ZERO_NUMBER,
  pageIndex = ZERO_NUMBER,
  pageSize = PAGE_SIZE_50,
  setPageIndex,
  setPageSize,
  columnOrders = {},
  handleSort = () => {}
}) => {
  const baseColumns = useMemo(() => COLUMNS, []);
  const sortableColumns = useMemo(
    () => baseColumns.map((column) => column.accessor),
    [baseColumns]
  );

  const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page } =
    useTable(
      {
        columns: baseColumns,
        data: data || [],
        initialState: { pageIndex, pageSize },
        manualPagination: true,
        autoResetPage: false
      },
      usePagination
    );

  const canPreviousPage = pageIndex > ZERO_NUMBER;
  const canNextPage =
    pageIndex < totalPages - NUMBER_ONE && data && data.length > ZERO_NUMBER;
  const pageOptions = useMemo(
    () => [...Array(totalPages).keys()],
    [totalPages]
  );

  const previousPage = () =>
    setPageIndex(Math.max(ZERO_NUMBER, pageIndex - NUMBER_ONE));

  const nextPage = () =>
    setPageIndex(
      pageIndex < totalPages - NUMBER_ONE ? pageIndex + NUMBER_ONE : pageIndex
    );

  return (
    <>
      {/* The row carries thirteen columns, so it scrolls sideways rather than dropping
          columns, which would take Payments Received and Balance Owing off the screen —
          the two the report exists to show. */}
      <div className="table-responsive">
        <table
          className="mt-2 mb-3 monthly-report-table bgless_table accounts-receivable-table w-100"
          {...getTableProps()}
        >
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key, ...rest } = headerGroup.getHeaderGroupProps();
              return (
                <tr className="parentrow" {...rest} key={key}>
                  {/* tableSortWithIcon draws the neutral sort icon on an unsorted
                      column too, so every column shows that it can be sorted rather
                      than only the column in use. */}
                  {headerGroup.headers.map((column) => (
                    <SortableHeaderCell
                      key={column.id}
                      column={column}
                      isSortable={sortableColumns.includes(column.id)}
                      columnOrders={columnOrders}
                      handleSort={handleSort}
                      tableSortWithIcon
                    />
                  ))}
                </tr>
              );
            })}
          </thead>

          <tbody {...getTableBodyProps()}>
            {loading && (
              <TableSkeleton
                rows={SKELETON_ROWS_10}
                columns={baseColumns.length}
              />
            )}
            {!loading && page.length === ZERO_NUMBER && (
              <tr>
                <td colSpan={baseColumns.length} className="text-center">
                  {noDataPresent}
                </td>
              </tr>
            )}
            {!loading &&
              page.map((row) => {
                prepareRow(row);
                return (
                  <tr className="parentrow" {...row.getRowProps()} key={row.id}>
                    {row.cells.map((cell) => (
                      <td key={cell.column.id} {...cell.getCellProps()}>
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {totalPages > ZERO_NUMBER && (
        <DualPagination
          gotoPage={setPageIndex}
          previousPage={previousPage}
          nextPage={nextPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageIndex={pageIndex}
          pageCount={totalPages}
          pageOptions={pageOptions}
          setPageSize={setPageSize}
          pageSize={pageSize}
          dataLength={data ? data.length : ZERO_NUMBER}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      )}
    </>
  );
};

AccountsReceivableTable.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  totalPages: PropTypes.number,
  pageIndex: PropTypes.number,
  pageSize: PropTypes.number,
  setPageIndex: PropTypes.func,
  setPageSize: PropTypes.func,
  columnOrders: PropTypes.object,
  handleSort: PropTypes.func
};

export default AccountsReceivableTable;
